import React, { useState } from 'react';
import TicketCard from './TicketCard';
import LoadingSpinner from './LoadingSpinner';
import './Sidebar.css';

function Sidebar({ tickets, onDrop, loading = false }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('Issue key-desc');

  // Always call hooks first, then handle loading
  if (loading) {
    return (
      <div className="sidebar">
        <h3>Backlog</h3>
        <LoadingSpinner message="Loading backlog..." />
      </div>
    );
  }

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    onDrop(e, 'unassigned');
  };

  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const filteredTickets = tickets.filter((ticket) => {
    const lowerSearch = searchTerm.toLowerCase();
    return (
      ticket.issue_key?.toLowerCase().includes(lowerSearch) ||
      ticket.summary?.toLowerCase().includes(lowerSearch) ||
      ticket.description?.toLowerCase().includes(lowerSearch) ||
      ticket.category?.toLowerCase().includes(lowerSearch) || // ✅ NEW SEARCH FIELD
      (ticket.initial_effort_estimations && ticket.initial_effort_estimations.toString().includes(searchTerm)) // ✅ NEW SEARCH FIELD
    );
  });

  const sortedTickets = [...filteredTickets].sort((a, b) => {
    const [field, order] = sortOption.split('-');

    let aValue, bValue;

    switch (field) {
      case 'Issue key':
        aValue = a.issue_key || '';
        bValue = b.issue_key || '';
        // Handle numeric sorting for issue keys
        const aNum = parseInt(aValue.split('-')[1], 10);
        const bNum = parseInt(bValue.split('-')[1], 10);
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return order === 'asc' ? aNum - bNum : bNum - aNum;
        }
        break;
      case 'Summary':
        aValue = (a.summary || '').toLowerCase();
        bValue = (b.summary || '').toLowerCase();
        break;
      case 'Created':
        aValue = new Date(a.created_at || 0);
        bValue = new Date(b.created_at || 0);
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      case 'Category': // ✅ NEW SORT OPTION
        aValue = (a.category || '').toLowerCase();
        bValue = (b.category || '').toLowerCase();
        break;
      case 'Effort': // ✅ NEW SORT OPTION
        aValue = a.initial_effort_estimations || 0;
        bValue = b.initial_effort_estimations || 0;
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      default:
        aValue = a[field.toLowerCase()] || '';
        bValue = b[field.toLowerCase()] || '';
    }

    if (field === 'Category' || field === 'Effort') {
      // Handle the new fields that we already processed above
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    }

    // Handle string comparison for other fields
    if (aValue < bValue) return order === 'asc' ? -1 : 1;
    if (aValue > bValue) return order === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div className="sidebar" onDragOver={handleDragOver} onDrop={handleDrop}>
      <h3>Backlog</h3>

      <div className="sidebar-controls">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search tickets, categories, effort..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-button" onClick={handleClearSearch}>✖</button>
          )}
        </div>

        <select value={sortOption} onChange={handleSortChange}>
          <option value="Issue key-asc">Issue Key ↑</option>
          <option value="Issue key-desc">Issue Key ↓</option>
          <option value="Summary-asc">Summary ↑</option>
          <option value="Summary-desc">Summary ↓</option>
          <option value="Created-asc">Created ↑</option>
          <option value="Created-desc">Created ↓</option>
          <option value="Category-asc">Category ↑</option>
          <option value="Category-desc">Category ↓</option>
          <option value="Effort-asc">Effort ↑</option>
          <option value="Effort-desc">Effort ↓</option>
        </select>
      </div>

      <div className="ticket-list">
        {sortedTickets.length === 0 ? (
          <div className="no-tickets">No tickets match your search.</div>
        ) : (
          sortedTickets.map((ticket) => (
            <TicketCard
              key={ticket.issue_key}
              ticket={ticket}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('ticket', JSON.stringify(ticket));
              }}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default Sidebar;